import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import api from "../api";
import Shell from "../Shell";
import { authHeaders } from "../api";
import "../dashboard.css";

/* ---------- small helpers ---------- */
function useDebounced(v, ms = 300) {
  const [s, setS] = useState(v);
  useEffect(() => {
    const t = setTimeout(() => setS(v), ms);
    return () => clearTimeout(t);
  }, [v, ms]);
  return s;
}

function TabBtn({ active, onClick, children }) {
  return (
    <button
      className={`btn ${active ? "" : "ghost"} sm`}
      style={{ borderRadius: 999, padding: "6px 12px", marginRight: 8 }}
      onClick={onClick}
    >
      {children}
    </button>
  );
}

function fmt(d) {
  if (!d) return "—";
  const dt = new Date(d);
  if (String(dt) === "Invalid Date") return d;
  const dd = String(dt.getDate()).padStart(2, "0");
  const mm = String(dt.getMonth() + 1).padStart(2, "0");
  const yyyy = dt.getFullYear();
  return `${dd}/${mm}/${yyyy}`;
}

function dateToIso(date) {
  if (!(date instanceof Date)) return "";
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, "0");
  const d = String(date.getDate()).padStart(2, "0");
  return `${y}-${m}-${d}`;
}

function startOfWeekMonday(date) {
  const d = new Date(date.getFullYear(), date.getMonth(), date.getDate());
  const day = (d.getDay() + 6) % 7; // Monday=0
  d.setDate(d.getDate() - day);
  return d;
}

function endOfWeekSunday(date) {
  const d = startOfWeekMonday(date);
  d.setDate(d.getDate() + 6);
  return d;
}

function firstDayOfMonth(date) {
  return new Date(date.getFullYear(), date.getMonth(), 1);
}

function lastDayOfMonth(date) {
  return new Date(date.getFullYear(), date.getMonth() + 1, 0);
}

function applyYesterday(setFromDate, setToDate) {
  const d = new Date();
  d.setDate(d.getDate() - 1);
  const iso = dateToIso(d);
  setFromDate(iso);
  setToDate(iso);
}

function applyLastWeek(setFromDate, setToDate) {
  const today = new Date();
  const thisWeekStart = startOfWeekMonday(today);
  const lastWeekStart = new Date(thisWeekStart);
  lastWeekStart.setDate(thisWeekStart.getDate() - 7);
  const lastWeekEnd = endOfWeekSunday(lastWeekStart);
  setFromDate(dateToIso(lastWeekStart));
  setToDate(dateToIso(lastWeekEnd));
}

function applyPastWeek(setFromDate, setToDate) {
  const today = new Date();
  const start = new Date(today.getFullYear(), today.getMonth(), today.getDate());
  start.setDate(start.getDate() - 6);
  setFromDate(dateToIso(start));
  setToDate(dateToIso(today));
}

function applyLastMonth(setFromDate, setToDate) {
  const today = new Date();
  const prevMonthDate = new Date(today.getFullYear(), today.getMonth() - 1, 1);
  setFromDate(dateToIso(firstDayOfMonth(prevMonthDate)));
  setToDate(dateToIso(lastDayOfMonth(prevMonthDate)));
}

function applyPastMonth(setFromDate, setToDate) {
  const today = new Date();
  const start = new Date(today.getFullYear(), today.getMonth(), today.getDate());
  start.setDate(start.getDate() - 29);
  setFromDate(dateToIso(start));
  setToDate(dateToIso(today));
}

function Table({ headers, rows, footer }) {
  return (
    <div className="table-wrap">
      <table className="table zebra">
        <thead>
          <tr>
            {headers.map((h, i) => (
              <th key={`head-${i}`}>{h}</th>
            ))}
          </tr>
        </thead>

        <tbody>
          {rows.length === 0 ? (
            <tr>
              <td colSpan={headers.length} style={{ padding: 16, textAlign: "center" }}>
                Engin gögn
              </td>
            </tr>
          ) : (
            rows.map((r, i) => (
              <tr key={`row-${i}`}>
                {r.map((c, j) => (
                  <td key={`cell-${i}-${j}`}>{c}</td>
                ))}
              </tr>
            ))
          )}
        </tbody>

        {footer ? (
          <tfoot>
            <tr>
              {footer.map((c, i) => (
                <td key={`foot-${i}`}>{c}</td>
              ))}
            </tr>
          </tfoot>
        ) : null}
      </table>
    </div>
  );
}

/* ---------- Editor modal ---------- */
function EditorModal({ open, title, value, setValue, onCancel, onSave }) {
  if (!open) return null;

  return (
    <div
      role="dialog"
      aria-modal="true"
      className="modal-backdrop"
      onClick={onCancel}
    >
      <div
        className="modal"
        onClick={(e) => e.stopPropagation()}
        style={{ width: "min(720px, 92vw)" }}
      >
        <h3 style={{ marginTop: 0 }}>{title}</h3>

        <textarea
          rows={8}
          value={value}
          onChange={(e) => setValue(e.target.value)}
          style={{ width: "100%", resize: "vertical" }}
          placeholder="Skrifaðu lýsingu á hópnum…"
        />

        <div style={{ display: "flex", justifyContent: "flex-end", gap: 8, marginTop: 12 }}>
          <button className="btn ghost sm" onClick={onCancel}>Hætta við</button>
          <button className="btn sm" onClick={onSave}>Vista</button>
        </div>
      </div>
    </div>
  );
}

/* ---------- AddLeader modal ---------- */
function AddLeaderModal({ open, groupId, existingIds = [], onClose, onAdded }) {
  const [q, setQ] = useState("");
  const dq = useDebounced(q, 300);
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!open) return;
    document.body.classList.add("modal-open");
    const onKey = (e) => e.key === "Escape" && onClose?.();
    document.addEventListener("keydown", onKey);
    return () => {
      document.body.classList.remove("modal-open");
      document.removeEventListener("keydown", onKey);
    };
  }, [open, onClose]);

  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const { data } = await api.get("/api/group-leaders/", {
          params: dq ? { q: dq } : {},
          headers: authHeaders(),
        });
        if (!cancelled) setRows(Array.isArray(data) ? data : []);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [open, dq]);

  const add = async (userId) => {
    try {
      await api.post(
        `/api/groups/${groupId}/add_leader/`,
        { user_id: userId },
        { headers: authHeaders() }
      );
      onAdded?.();
      onClose?.();
    } catch (e) {
      alert(e?.response?.data?.detail || "Tókst ekki að bæta við hópstjóra.");
    }
  };

  if (!open) return null;

  return (
    <div className="modal-backdrop" onClick={onClose} role="presentation">
      <div
        className="modal"
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-labelledby="add-leader-title"
      >
        <h3 id="add-leader-title" style={{ marginTop: 0 }}>Bæta hópstjóra</h3>

        <input
          autoFocus
          placeholder="Leita (nafn, netfang, notendanafn, sími)"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          style={{ width: "100%", marginBottom: 10 }}
        />

        <div className="table-wrap" style={{ maxHeight: 320, overflow: "auto" }}>
          <table className="table zebra">
            <thead>
              <tr>
                <th>Nafn</th>
                <th>Notendanafn</th>
                <th>Netfang</th>
                <th>Sími</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan={5} style={{ textAlign: "center", padding: 12 }}>Hleð…</td>
                </tr>
              ) : rows.length === 0 ? (
                <tr>
                  <td colSpan={5} style={{ textAlign: "center", padding: 12 }}>
                    Engir hópstjórar fundust
                  </td>
                </tr>
              ) : (
                rows.map((u) => {
                  const already = existingIds.includes(u.id);
                  return (
                    <tr key={u.id}>
                      <td>{u.full_name}</td>
                      <td>{u.username}</td>
                      <td>{u.email}</td>
                      <td>{u.phone_number}</td>
                      <td style={{ whiteSpace: "nowrap" }}>
                        <button className="btn sm" disabled={already} onClick={() => add(u.id)}>
                          {already ? "Í hópnum" : "Bæta"}
                        </button>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>

        <div style={{ textAlign: "right", marginTop: 10 }}>
          <button className="btn ghost sm" onClick={onClose}>Loka</button>
        </div>
      </div>
    </div>
  );
}

/* ============================== PAGE ============================== */

export default function GroupDetail() {
  const { id } = useParams();
  const nav = useNavigate();

  const [role, setRole] = useState("staff");
  const canManage = role === "admin" || role === "staff";

  const [tab, setTab] = useState("overview"); // overview | members | attendance
  const [summary, setSummary] = useState(null);
  const [rows, setRows] = useState([]);

  const [leaders, setLeaders] = useState([]);
  const [addOpen, setAddOpen] = useState(false);

  // description editor
  const [descOpen, setDescOpen] = useState(false);
  const [descValue, setDescValue] = useState("");

  // attendance filters
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");
  const [order, setOrder] = useState("desc");
  const [uniqueUsersOnly, setUniqueUsersOnly] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const r = await api.get("/api/user/role/", { headers: authHeaders() });
        setRole(r.data.role);
      } catch {}
    })();
  }, []);

  const loadSummary = useCallback(async () => {
    const { data } = await api.get(`/api/groups/${id}/summary/`, { headers: authHeaders() });
    setSummary(data);
    setDescValue(data?.description || "");
    if (Array.isArray(data?.leaders)) setLeaders(data.leaders);
  }, [id]);

  const loadLeaders = useCallback(async () => {
    const { data } = await api.get(`/api/groups/${id}/leaders/`, { headers: authHeaders() });
    setLeaders(Array.isArray(data) ? data : []);
  }, [id]);

  useEffect(() => {
    loadSummary();
  }, [loadSummary]);

  useEffect(() => {
    (async () => {
      if (!summary) return;

      let url = "";

      if (tab === "members") {
        url = `/api/groups/${id}/members/`;
      } else if (tab === "attendance") {
        const p = new URLSearchParams();
        if (fromDate) p.set("from", fromDate);
        if (toDate) p.set("to", toDate);
        if (order) p.set("order", order);
        url = `/api/groups/${id}/attendance/${p.toString() ? `?${p}` : ""}`;
      }

      if (!url) {
        setRows([]);
        return;
      }

      const { data } = await api.get(url, { headers: authHeaders() });
      setRows(Array.isArray(data) ? data : []);
    })();
  }, [tab, summary, id, fromDate, toDate, order]);

  /* ------- admin/staff actions ------- */
  const toggleActive = async () => {
    const newVal = !summary.is_active;
    await api.post(
      `/api/groups/${id}/toggle_active/`,
      { is_active: newVal },
      { headers: authHeaders() }
    );
    setSummary({ ...summary, is_active: newVal });
  };

  const saveDescription = async () => {
    try {
      await api.patch(
        `/api/groups/${id}/`,
        { description: descValue },
        { headers: authHeaders() }
      );
      setSummary((s) => ({ ...s, description: descValue }));
      setDescOpen(false);
    } catch (e) {
      alert(e?.response?.data?.detail || "Tókst ekki að vista lýsingu hóps.");
    }
  };

  const removeLeader = async (leaderId) => {
    if (!window.confirm("Afskrá hópstjóra?")) return;
    await api.post(
      `/api/groups/${id}/remove_leader/`,
      { user_id: leaderId },
      { headers: authHeaders() }
    );
    await Promise.all([loadSummary(), loadLeaders()]);
  };

  const removeMember = async (userId) => {
    if (!window.confirm("Fjarlægja meðlim úr hóp?")) return;
    try {
      await api.post(
        `/api/groups/${id}/remove_member/`,
        { user_id: userId },
        { headers: authHeaders() }
      );
      setRows((prev) => prev.filter((r) => String(r.user_id) !== String(userId)));
    } catch (e) {
      alert(e?.response?.data?.detail || "Tókst ekki að fjarlægja meðlim úr hóp.");
    }
  };

  const deleteGroup = async () => {
    if (!window.confirm("Ertu viss um að eyða hópnum? Þetta er óafturkræft.")) return;
    await api.delete(`/api/groups/${id}/`, { headers: authHeaders() });
    nav("/groups");
  };

  const memberCount = useMemo(() => (
    tab === "members" ? rows.length : 0
  ), [rows, tab]);

  

  const membersTotalAttendance = useMemo(
    () => rows.reduce((sum, r) => sum + (r.count || 0), 0),
    [rows]
  );

  const displayedAttendanceRows = useMemo(() => {
    if (tab !== "attendance") return rows;
    if (!uniqueUsersOnly) return rows;

    const latestByUsername = new Map();

    for (const row of rows) {
      const key = String(row.username || "").trim().toLowerCase();
      if (!key) continue;

      const prev = latestByUsername.get(key);
      const rowTime = row.date ? new Date(row.date).getTime() : 0;
      const prevTime = prev?.date ? new Date(prev.date).getTime() : 0;

      if (!prev || rowTime > prevTime) {
        latestByUsername.set(key, row);
      }
    }

    const deduped = Array.from(latestByUsername.values());

    deduped.sort((a, b) => {
      const aTime = a.date ? new Date(a.date).getTime() : 0;
      const bTime = b.date ? new Date(b.date).getTime() : 0;

      if (order === "asc") {
        if (aTime !== bTime) return aTime - bTime;
      } else {
        if (aTime !== bTime) return bTime - aTime;
      }

      return String(a.user || a.username || "").localeCompare(
        String(b.user || b.username || ""),
        "is"
      );
    });

    return deduped;
  }, [rows, tab, uniqueUsersOnly, order]);

  const attendanceCount = useMemo(() => (
    tab === "attendance" ? displayedAttendanceRows.length : 0
  ), [displayedAttendanceRows, tab]);

  return (
    <Shell title="Hugarafl starfsfólk" rightMenu>
      {!summary ? (
        <div>Hleð…</div>
      ) : (
        <>
          <h2 style={{ margin: "6px 0 14px" }}>{summary.name}</h2>

          <div style={{ display: "flex", gap: 8, marginBottom: 12, flexWrap: "wrap" }}>
            <TabBtn active={tab === "overview"} onClick={() => setTab("overview")}>
              Yfirlit á hóp
            </TabBtn>
            <TabBtn active={tab === "members"} onClick={() => setTab("members")}>
              Hópmeðlimir
            </TabBtn>
            <TabBtn active={tab === "attendance"} onClick={() => setTab("attendance")}>
              Mæting
            </TabBtn>
          </div>

          {tab === "overview" && (
            <div className="placeholder">
              <div style={{ margin: "10px 0" }}>
                <div style={{ opacity: 0.7 }}>Er hópurinn virkur:</div>
                <div>{summary.is_active ? "Já" : "Nei"}</div>
                {canManage && (
                  <div style={{ marginTop: 8 }}>
                    <button className="btn sm" onClick={toggleActive}>
                      {summary.is_active ? "Óvirkja hóp" : "Virkja hóp"}
                    </button>
                  </div>
                )}
              </div>

              <div style={{ margin: "10px 0" }}>
                <div style={{ opacity: 0.7, marginBottom: 6 }}>Lýsing hóps:</div>
                <div style={{ whiteSpace: "pre-wrap" }}>{summary.description || "—"}</div>
                {canManage && (
                  <div style={{ marginTop: 8 }}>
                    <button className="btn sm ghost" onClick={() => setDescOpen(true)}>
                      Breyta lýsingu hóps
                    </button>
                  </div>
                )}
              </div>

              <div style={{ margin: "10px 0" }}>
                <div style={{ opacity: 0.7, marginBottom: 6 }}>Hópstjórar:</div>
                {leaders.length === 0 ? (
                  <div>—</div>
                ) : (
                  leaders.map((l) => (
                    <div
                      key={l.id}
                      style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 4 }}
                    >
                      <span>{l.full_name}</span>
                      {canManage && (
                        <button
                          className="btn ghost sm danger"
                          onClick={() => removeLeader(l.id)}
                        >
                          Afskrá hópstjóra
                        </button>
                      )}
                    </div>
                  ))
                )}
                {canManage && (
                  <div style={{ marginTop: 6 }}>
                    <button className="btn sm ghost" onClick={() => setAddOpen(true)}>
                      Bæta hópstjóra
                    </button>
                  </div>
                )}
              </div>

              <div style={{ margin: "10px 0" }}>
                <div style={{ opacity: 0.7 }}>Síðast skráð:</div>
                <div>{fmt(summary.last_held)}</div>
              </div>

              <div style={{ margin: "10px 0" }}>
                <div style={{ opacity: 0.7 }}>Hópur stofnaður:</div>
                <div>{fmt(summary.created_at)}</div>
              </div>

              {canManage && (
                <div style={{ marginTop: 18 }}>
                  <button className="btn ghost danger" onClick={deleteGroup}>
                    Eyða hóp
                  </button>
                </div>
              )}
            </div>
          )}

          {tab === "members" && (
            <>
              <div className="members-toolbar" style={{ justifyContent: "center", marginBottom: 12 }}>
                <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                  <div style={{
                    border: "2px solid #222",
                    borderRadius: 6,
                    padding: "6px 12px",
                    textAlign: "center",
                    minWidth: 180,
                    fontWeight: 600
                  }}>
                    Hópmeðlimir samtals
                    <div style={{ fontSize: 18 }}>{memberCount}</div>
                  </div>
                </div>
              </div>

              <Table
                headers={["Nafn", "Mæting", "Mætti síðast", "", ...(canManage ? [""] : [])]}
                rows={rows.map((r) => [
                  r.name,
                  r.count,
                  fmt(r.last),
                  <Link className="btn sm ghost" to={`/members/${r.user_id}`}>
                    Nánar
                  </Link>,
                  ...(canManage ? [
                    <button
                      className="btn sm ghost danger"
                      onClick={() => removeMember(r.user_id)}
                    >
                      Fjarlægja úr hóp
                    </button>
                  ] : []),
                ])}
                footer={["Mæting í hóp alls", membersTotalAttendance, "", ...(canManage ? ["", ""] : [""])]}
              />
            </>
          )}

          {tab === "attendance" && (
            <>
              <div className="members-toolbar" style={{ justifyContent: "space-between", marginBottom: 12 }}>
                <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                  <div style={{
                    border: "2px solid #222",
                    borderRadius: 6,
                    padding: "6px 12px",
                    textAlign: "center",
                    minWidth: 180,
                    fontWeight: 600
                  }}>
                    Mæting samtals
                    <div style={{ fontSize: 18 }}>{attendanceCount}</div>
                  </div>
                </div>

                <div className="filter-block">
                  <button className="btn sm" onClick={() => setFiltersOpen((v) => !v)}>
                    Sía/röð
                  </button>

                  {filtersOpen && (
                    <div className="filter-popover" style={{ minWidth: 360 }}>
                      <div className="filter-title">Sía eftir dagsetningum:</div>

                      <div className="filter-row">
                        <label>Byrjunardagsetning:</label>
                        <input
                          type="date"
                          value={fromDate}
                          onChange={(e) => setFromDate(e.target.value)}
                        />
                      </div>

                      <div className="filter-row">
                        <label>Endadagsetning:</label>
                        <input
                          type="date"
                          value={toDate}
                          onChange={(e) => setToDate(e.target.value)}
                        />
                      </div>

                      <div className="filter-title" style={{ marginTop: 8 }}>
                        Flýtihnappar:
                      </div>

                      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8, marginTop: 6 }}>
                        <button className="btn ghost sm" onClick={() => applyYesterday(setFromDate, setToDate)}>
                          Gær
                        </button>

                        <button className="btn ghost sm" onClick={() => applyLastWeek(setFromDate, setToDate)}>
                          Síðasta vika
                        </button>

                        <button className="btn ghost sm" onClick={() => applyPastWeek(setFromDate, setToDate)}>
                          Síðustu 7 dagar
                        </button>

                        <button className="btn ghost sm" onClick={() => applyLastMonth(setFromDate, setToDate)}>
                          Síðasti mánuður
                        </button>

                        <button className="btn ghost sm" onClick={() => applyPastMonth(setFromDate, setToDate)}>
                          Síðustu 30 dagar
                        </button>
                      </div>

                      <div className="filter-title" style={{ marginTop: 8 }}>
                        Fleiri síur:
                      </div>

                      <label style={{ display: "inline-flex", alignItems: "center", gap: 8, marginTop: 4 }}>
                        <input
                          type="checkbox"
                          checked={uniqueUsersOnly}
                          onChange={(e) => setUniqueUsersOnly(e.target.checked)}
                        />
                        Sýna aðeins einstaka notendur
                      </label>

                      <div className="filter-title" style={{ marginTop: 8 }}>
                        Raða eftir:
                      </div>
                      <select value={order} onChange={(e) => setOrder(e.target.value)}>
                        <option value="desc">Nýjast fyrst</option>
                        <option value="asc">Elst fyrst</option>
                      </select>

                      <div style={{ display: "flex", justifyContent: "flex-end", gap: 8, marginTop: 10 }}>
                        <button
                          className="btn ghost sm"
                          onClick={() => {
                            setFromDate("");
                            setToDate("");
                            setOrder("desc");
                            setUniqueUsersOnly(false);
                          }}
                        >
                          Hreinsa
                        </button>
                        <button className="btn ghost sm" onClick={() => setFiltersOpen(false)}>
                          Loka síu
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              </div>

              <Table
                headers={["Nafn", "Skráði mætingu", "Dagsetning"]}
                rows={displayedAttendanceRows.map((x) => [x.user, x.marked_by || "—", fmt(x.date)])}
              />
            </>
          )}

          <EditorModal
            open={descOpen}
            title="Breyta lýsingu hóps"
            value={descValue}
            setValue={setDescValue}
            onCancel={() => setDescOpen(false)}
            onSave={saveDescription}
          />

          <AddLeaderModal
            open={addOpen}
            groupId={id}
            existingIds={leaders.map((l) => l.id)}
            onClose={() => setAddOpen(false)}
            onAdded={() => {
              loadLeaders();
              loadSummary();
            }}
          />
        </>
      )}
    </Shell>
  );
}